import React from 'react';
import { DealDetail, DealStage } from '../../../types';
import { DEAL_STAGES } from '../utils/dealStages';
import api, { dealsApi } from '../../../services/api';
import { showSuccess } from '../../../utils/toast';

interface DealPanelHeaderProps {
  viewMode: 'view' | 'edit' | null;
  selectedDeal: DealDetail;
  submitting: boolean;
  creatingProject: number | null;
  setCreatingProject: (id: number | null) => void;
  setPanelError: (msg: string | null) => void;
  setSelectedDeal: (deal: DealDetail) => void;
  handleStageTransition: (stage: DealStage) => void;
  navigate: (path: string) => void;
}

const DealPanelHeader: React.FC<DealPanelHeaderProps> = ({
  viewMode,
  selectedDeal,
  submitting,
  creatingProject,
  setCreatingProject,
  setPanelError,
  setSelectedDeal,
  handleStageTransition,
  navigate,
}) => {
  if (viewMode !== 'view') return undefined as any;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded hover:shadow-lg transition-shadow text-sm font-medium"
      >
        View Quote
      </button>
      <button
        type="button"
        className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded hover:shadow-lg transition-shadow text-sm font-medium"
      >
        Send Quote
      </button>
      {selectedDeal.stage === 'Closed Won' && (
        <button
          type="button"
          className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded hover:shadow-lg transition-shadow disabled:opacity-50 text-sm font-medium"
          disabled={creatingProject === selectedDeal.id || !!selectedDeal.project}
          onClick={async () => {
            try {
              setCreatingProject(selectedDeal.id);
              setPanelError(null);
              const response = await api.post(`/projects/create-from-deal/${selectedDeal.id}`);

              // Show success toast
              showSuccess('Project created successfully!');

              // Get the created project ID from response
              const newProjectId = (response as any)?.data?.id || (response as any)?.project?.id;

              // Reload the deal to get updated project relationship
              const updatedDeal = await dealsApi.getById(selectedDeal.id) as DealDetail;
              setSelectedDeal(updatedDeal);

              // Redirect to projects page with highlight parameter
              if (newProjectId) {
                navigate(`/projects?highlight=${newProjectId}`);
              } else {
                // Fallback: just navigate to projects page
                navigate('/projects');
              }
            } catch (e: any) {
              setPanelError(e?.response?.data?.message || e?.message || 'Project creation failed');
            } finally {
              setCreatingProject(null);
            }
          }}
        >
          {creatingProject === selectedDeal.id ? 'Creating...' : selectedDeal.project ? 'Project Created' : 'Create Project'}
        </button>
      )}
      <label className="text-sm font-medium theme-text-secondary">Status:</label>
      <select
        value={selectedDeal.stage}
        onChange={(e) => handleStageTransition(e.target.value as DealStage)}
        disabled={submitting}
        className="px-3 py-1.5 text-sm theme-border rounded-md theme-bg-primary theme-text-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {DEAL_STAGES.map((stage) => (
          <option key={stage} value={stage}>
            {stage}
          </option>
        ))}
      </select>
    </div>
  );
};

export default DealPanelHeader;
